import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Shield, AlertTriangle } from "lucide-react";

export default function PanelistRegister() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", mobile_number: "",
    gender: "", postal_code: "", date_of_birth: "", notification_preference: "email",
  });
  const [error, setError] = useState(null);
  const [ageBlock, setAgeBlock] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.post("/auth/register/panelist", form);
      await refresh();
      nav("/panelist");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (typeof detail === "string" && detail.toLowerCase().includes("18 or older")) {
        setAgeBlock(true);
      } else {
        setError(formatApiErrorDetail(detail) || e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (ageBlock) {
    return (
      <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="age-gate-screen">
        <Navigation />
        <div className="max-w-xl mx-auto px-6 py-24">
          <div className="bg-white border-2 border-[var(--rl-accent)] p-10">
            <AlertTriangle className="w-12 h-12 text-[var(--rl-accent)]" />
            <h1 className="font-display font-black text-3xl mt-6">We're sorry — you don't qualify yet.</h1>
            <p className="text-base text-[var(--rl-text-secondary)] mt-4 leading-relaxed">
              Adrigo Insights AI requires panelists to be 18 or older to participate in commercial research and receive digital rewards.
            </p>
            <Link to="/" className="inline-block mt-8 text-[var(--rl-brand)] font-semibold" data-testid="age-gate-home-link">← Return home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="panelist-register-page">
      <Navigation />
      <div className="max-w-2xl mx-auto px-6 py-14">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Earn from your opinion</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2">Join the Panel.</h1>
        <p className="text-[var(--rl-text-secondary)] mt-3 max-w-lg leading-relaxed">$2.00 CAD per 5-minute interview, capped at 4 studies per month. Atlantic Canada first.</p>

        <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-5" data-testid="panelist-register-form">
          {[
            ["full_name", "Full name", "text"],
            ["email", "Email", "email"],
            ["password", "Password", "password"],
            ["mobile_number", "Mobile number (e.g. +1902555…)", "tel"],
            ["postal_code", "Postal code (e.g. B3J 2K9)", "text"],
            ["date_of_birth", "Date of birth", "date"],
          ].map(([k, label, type]) => (
            <div key={k} className={k === "full_name" || k === "date_of_birth" ? "sm:col-span-2" : ""}>
              <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-2">{label}</label>
              <input
                type={type}
                required
                value={form[k]}
                onChange={set(k)}
                className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)] transition-colors"
                data-testid={`panelist-input-${k}`}
              />
            </div>
          ))}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-2">Gender</label>
            <select
              required
              value={form.gender}
              onChange={set("gender")}
              className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
              data-testid="panelist-input-gender"
            >
              <option value="">Select…</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="nonbinary">Non-binary</option>
              <option value="prefer_not">Prefer not to say</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-2">Notify me via</label>
            <select
              value={form.notification_preference}
              onChange={set("notification_preference")}
              className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
              data-testid="panelist-input-notification"
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </div>

          {error && (
            <div className="sm:col-span-2 text-sm text-[var(--rl-accent)] font-medium" data-testid="panelist-register-error">{error}</div>
          )}

          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--rl-text)] text-white py-4 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
              data-testid="panelist-register-submit"
            >
              {loading ? "Creating your account…" : "Create panelist account"}
            </button>
            <div className="mt-4 text-center text-xs text-[var(--rl-text-secondary)]" data-testid="panelist-register-existing">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-[var(--rl-brand)] hover:underline" data-testid="link-panelist-login">Sign in</Link>
              <span className="mx-2 text-[var(--rl-border)]">·</span>
              <Link to="/forgot-password" className="font-semibold text-[var(--rl-brand)] hover:underline" data-testid="link-panelist-forgot-password">Forgot password?</Link>
            </div>
          </div>
        </form>

        <div className="mt-8 flex items-start gap-3 bg-white border border-[var(--rl-border)] p-5" data-testid="pipeda-footer">
          <Shield className="w-5 h-5 text-[var(--rl-brand)] flex-shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed text-[var(--rl-text-secondary)]">
            Adrigo Insights AI is fully <strong className="text-[var(--rl-text)]">PIPEDA compliant</strong>. Your data is entirely anonymized, and voice prints are strictly used for transcription verification.
          </p>
        </div>
      </div>
    </div>
  );
}
